import { useFormik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { WorkplaceFields } from "../../../../api/graphql/workplaces";

export interface GroupFormFields {
  name: string;
  description: string;
  accessEverything: boolean;
  rooms: WorkplaceFields[];
  desks: WorkplaceFields[];
  locations: string[];
  members: string[];
  deleteMembers?: string[];
  addMembers?: string[];
  deskBookingEnabled: boolean;
  roomBookingEnabled: boolean;
  id?: string;
  isDefault?: boolean;
}

interface UseGroupFormikOptions {
  initialValues?: Partial<GroupFormFields>;
  enableReinitialize?: boolean;
  onSubmit: (
    values: GroupFormFields,
    formikHelpers: FormikHelpers<GroupFormFields>
  ) => Promise<void>;
}

const GroupFormSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Group name must be at least 2 characters")
    .required("Group name is required"),
  description: Yup.string(),
  accessEverything: Yup.boolean(),
  rooms: Yup.array().of(
    Yup.object().shape({
      id: Yup.string(),
      name: Yup.string(),
    })
  ),
  desks: Yup.array().of(
    Yup.object().shape({
      id: Yup.string(),
      name: Yup.string(),
    })
  ),
  locations: Yup.array().of(Yup.string()),
  members: Yup.array().of(Yup.string()),
  deleteMembers: Yup.array().of(Yup.string()),
  addMembers: Yup.array().of(Yup.string()),
  deskBookingEnabled: Yup.boolean(),
  roomBookingEnabled: Yup.boolean(),
});

export type GroupFormik = ReturnType<typeof useGroupFormik>;

export const useGroupFormik = (opts: UseGroupFormikOptions) => {
  return useFormik<GroupFormFields>({
    initialValues: {
      name: "",
      description: "",
      accessEverything: true,
      rooms: [],
      desks: [],
      locations: [],
      members: [],
      deleteMembers: [],
      addMembers: [],
      deskBookingEnabled: true,
      roomBookingEnabled: true,
      id: "",
      isDefault: false,
      ...opts.initialValues,
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: GroupFormSchema,
    enableReinitialize: opts.enableReinitialize ?? true,
    onSubmit: async (values, formikHelpers) => {
      await opts.onSubmit(values, formikHelpers);
    },
  });
};
